import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { toast } from 'sonner';
import {
  pipelineService,
  ASSET_TIPOS,
  ASSET_CATEGORIAS,
  CANAIS,
  tipoAssetMeta,
  categoriaAssetMeta,
  canalMeta,
  type PipelineAsset,
  type AssetTipo,
  type AssetCategoria,
  type PipelineCanal,
} from '@/features/pipeline/services/pipeline.service';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Plus, Search, Copy, Trash2, Download, ExternalLink, Pencil } from 'lucide-react';

export const Route = createFileRoute('/_authenticated/pipeline/assets/')({
  component: AssetsList,
});

function AssetsList() {
  return (
    <PermissionGate permission={Permission.PIPELINE_VIEW}>
      <AssetsListContent />
    </PermissionGate>
  );
}

function AssetsListContent() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data = [] } = useQuery({ queryKey: ['pipeline-assets'], queryFn: pipelineService.listAssets });
  const [q, setQ] = useState('');
  const [tipo, setTipo] = useState<string>('todos');
  const [categoria, setCategoria] = useState<string>('todos');
  const [canal, setCanal] = useState<string>('todos');

  const filtered = useMemo(() => {
    return data.filter((a) => {
      if (tipo !== 'todos' && a.tipo !== tipo) return false;
      if (categoria !== 'todos' && a.categoria !== categoria) return false;
      if (canal !== 'todos' && !a.canaisSugeridos.includes(canal as PipelineCanal)) return false;
      if (q) {
        const t = q.toLowerCase();
        if (
          !a.titulo.toLowerCase().includes(t) &&
          !(a.descricao ?? '').toLowerCase().includes(t) &&
          !a.tags.some((tg) => tg.toLowerCase().includes(t))
        )
          return false;
      }
      return true;
    });
  }, [data, q, tipo, categoria, canal]);

  const delM = useMutation({
    mutationFn: pipelineService.deleteAsset,
    onSuccess: () => {
      toast.success('Asset removido');
      qc.invalidateQueries({ queryKey: ['pipeline-assets'] });
    },
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Assets</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Biblioteca de mensagens, apresentações e materiais reutilizáveis.
          </p>
        </div>
        <Link
          to="/pipeline/assets/novo"
          className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> Novo asset
        </Link>
      </div>

      <Tabs value={tipo} onValueChange={setTipo}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="todos">Todos</TabsTrigger>
          {ASSET_TIPOS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              <t.icon className="h-3.5 w-3.5 mr-1.5" />
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por título, descrição ou tag..."
            className="pl-8"
          />
        </div>
        <Select value={categoria} onValueChange={setCategoria}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas as categorias</SelectItem>
            {ASSET_CATEGORIAS.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={canal} onValueChange={setCanal}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Canal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os canais</SelectItem>
            {CANAIS.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <p className="text-sm text-muted-foreground mb-3">Nenhum asset encontrado.</p>
            <Link
              to="/pipeline/assets/novo"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <Plus className="h-4 w-4" /> Criar seu primeiro asset
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => (
            <AssetCard
              key={a.id}
              a={a}
              onDelete={async () => {
                if (
                  await confirm({
                    title: `Remover "${a.titulo}"?`,
                    description: 'Esta ação não pode ser desfeita.',
                    confirmLabel: 'Remover asset',
                    destructive: true,
                  })
                )
                  delM.mutate(a.id);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AssetCard({ a, onDelete }: { a: PipelineAsset; onDelete: () => void }) {
  const tm = tipoAssetMeta(a.tipo as AssetTipo);
  const cm = categoriaAssetMeta(a.categoria as AssetCategoria);
  const Ic = tm.icon;

  async function copyText() {
    if (!a.conteudoTexto) return;
    await navigator.clipboard.writeText(a.conteudoTexto);
    toast.success('Texto copiado');
  }

  return (
    <Card className="group hover:border-primary/50 transition flex flex-col">
      <CardContent className="p-4 flex-1 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-md bg-primary/15 text-primary shrink-0">
            <Ic className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-medium text-sm truncate">{a.titulo}</h3>
              <Badge variant="secondary" className="text-[10px] shrink-0">
                {a.usoCount} usos
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1 mt-1">
              <Badge variant="outline" className="text-[10px]">
                {tm.label}
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {cm.label}
              </Badge>
            </div>
          </div>
        </div>

        {a.descricao && <p className="text-xs text-muted-foreground line-clamp-2">{a.descricao}</p>}

        {a.tipo === 'mensagem' && a.conteudoTexto && (
          <div className="rounded-md bg-secondary/50 p-2 text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap">
            {a.conteudoTexto}
          </div>
        )}
        {a.arquivoNome && <div className="text-xs text-muted-foreground truncate">📎 {a.arquivoNome}</div>}
        {a.urlExterna && <div className="text-xs text-muted-foreground truncate">🔗 {a.urlExterna}</div>}

        {a.canaisSugeridos.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {a.canaisSugeridos.slice(0, 5).map((c) => {
              const m = canalMeta(c);
              const Cc = m.icon;
              return (
                <span key={c} title={m.label} className="grid h-5 w-5 place-items-center rounded bg-secondary">
                  <Cc className="h-3 w-3" />
                </span>
              );
            })}
          </div>
        )}

        <div className="flex gap-1 mt-auto pt-2 border-t border-border opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition">
          {a.tipo === 'mensagem' && a.conteudoTexto && (
            <Button variant="ghost" size="sm" onClick={copyText} aria-label="Copiar texto">
              <Copy className="h-3.5 w-3.5" />
            </Button>
          )}
          {a.arquivoUrl && (
            <a href={a.arquivoUrl} target="_blank" rel="noreferrer">
              <Button variant="ghost" size="sm" aria-label="Baixar arquivo">
                <Download className="h-3.5 w-3.5" />
              </Button>
            </a>
          )}
          {a.urlExterna && (
            <a href={a.urlExterna} target="_blank" rel="noreferrer">
              <Button variant="ghost" size="sm" aria-label="Abrir link">
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </a>
          )}
          <Link to="/pipeline/assets/$id" params={{ id: a.id }}>
            <Button variant="ghost" size="sm" aria-label="Editar asset">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={onDelete} className="ml-auto text-destructive hover:text-destructive" aria-label="Remover asset">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
